// Chips for references in a body drawn as static HTML (the stored text shown
// before live editing syncs, and read-only documents). The HTML comes from the
// editor's serializer, where a reference is a plain
// `<span data-trameria-link …>name</span>`; each one gets the same `RefChip` as
// the live editor, rendered into the span through a portal, so the static view
// and the editor show the same chip and nothing moves when they swap.
import {
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { RefChip } from "./ref-chip";

interface Slot {
  el: HTMLElement;
  kind: string | null;
  id: string | null;
  label: string;
}

const attr = (el: HTMLElement, name: string): string | null => {
  const v = el.getAttribute(name);
  return v === null || v === "" ? null : v;
};

/** Portals a chip into every reference span under `root`; `html` is what was rendered. */
export function StaticRefChips({
  root,
  html,
}: {
  root: RefObject<HTMLElement | null>;
  html: string | null;
}): ReactNode {
  const [slots, setSlots] = useState<Slot[]>([]);
  // frontend-development: not a fetch; finds the reference spans of HTML just rendered.
  useLayoutEffect(() => {
    const box = root.current;
    if (!box || html === null) {
      setSlots([]);
      return;
    }
    const found: Slot[] = [];
    for (const el of box.querySelectorAll<HTMLElement>(
      "span[data-trameria-link]",
    )) {
      // The written name, kept before the span is emptied for the chip.
      if (el.dataset.refLabel === undefined)
        el.dataset.refLabel = el.textContent ?? "";
      el.textContent = "";
      found.push({
        el,
        kind: attr(el, "data-trameria-link-kind"),
        id: attr(el, "data-trameria-link-ref-id"),
        label: el.dataset.refLabel,
      });
    }
    setSlots(found);
  }, [root, html]);
  return (
    <>
      {slots.map((s, i) =>
        createPortal(
          <RefChip kind={s.kind} id={s.id} label={s.label} />,
          s.el,
          String(i),
        ),
      )}
    </>
  );
}
